import React, { useState, useEffect, useMemo, useCallback } from "react";

//* Import components from React Bootstrap
import { Button, Form, Spinner, Alert, Row, Col } from "react-bootstrap";

//* Import icons from lucide-react
import {
  ArrowLeft,
  Save,
  UserPlus,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";

//* Import Select from react-select
import Select from "react-select";

//* Import hooks from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//* Import custom hooks
import { useTeams } from "../../../hooks/teams/useTeams";
import { useCourses } from "../../../hooks/courses/useCourses";
import { useUnits } from "../../../hooks/units/useUnits";

//* Import hooks from react-query
import { useQueryClient } from "@tanstack/react-query";



type OptionType = { label: string; value: string | number };
type Params = { id: string };

const SHIFTS = [
  { label: "Manhã", value: "MORNING" },
  { label: "Tarde", value: "AFTERNOON" },
  { label: "Noite", value: "NIGHT" },
  { label: "Integral", value: "FULL" },
] as const;

const INITIAL_FORM_STATE = {
  name: "",
  period: "1",
  shift: "MORNING",
  startDate: "",
  endDate: "",
  courseId: null as OptionType | null,
  unitId: null as OptionType | null,
};

export function ClassForm({ editMode = false }: { editMode?: boolean }) {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();
  const params = useParams<Params>();
  const queryClient = useQueryClient();

  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Custom hooks
  const { createTeam, updateTeam, getTeamById } = useTeams(token);
  const { courses } = useCourses(token);
  const { units } = useUnits(token);

  // Memoized options
  const courseOptions = useMemo(
    () => courses.map((course) => ({ label: course.name, value: course.id })),
    [courses]
  );

  const unitOptions = useMemo(
    () => units.map((unit) => ({ label: unit.name, value: unit.id })),
    [units]
  );

  // Validation function for individual fields
  const validateField = useCallback(
    (field: string, value: any) => {
      const errors = { ...fieldErrors };

      switch (field) {
        case "name":
          if (!value || value.trim().length < 3) {
            errors.name =
              "Nome é obrigatório e deve ter no mínimo 3 caracteres.";
          } else {
            delete errors.name;
          }
          break;
        case "startDate":
          const selectedStartDate = new Date(value);

          if (
            formData.endDate &&
            selectedStartDate > new Date(formData.endDate)
          ) {
            errors.startDate =
              "Data de início não pode ser depois da data de fim.";
          } else {
            delete errors.startDate;
          }
          break;
        case "endDate":
          if (
            formData.startDate &&
            new Date(value) < new Date(formData.startDate)
          ) {
            errors.endDate =
              "Data de fim não pode ser antes da data de início.";
          } else {
            delete errors.endDate;
          }
          break;
        case "courseId":
          if (!value) {
            errors.courseId = "Curso é obrigatório.";
          } else {
            delete errors.courseId;
          }
          break;
        case "unitId":
          if (!value) {
            errors.unitId = "Unidade é obrigatória.";
          } else {
            delete errors.unitId;
          }
          break;
        default:
          break;
      }
      setFieldErrors(errors);
    },
    [fieldErrors, formData.endDate, formData.startDate]
  );

  // Handle form changes with validation
  const handleFormChange = useCallback(
    (field: keyof typeof INITIAL_FORM_STATE, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      validateField(field, value);
    },
    [validateField]
  );

  // Load team data for edit mode
  useEffect(() => {
    if (editMode && params.id) {
      let isMounted = true;

      const loadTeamData = async () => {
        setIsLoading(true);
        try {
          const teamDetails = await getTeamById(params.id!);
          if (teamDetails && isMounted) {
            setFormData({
              name: teamDetails.name,
              period: teamDetails.is_first_semester ? "1" : "2",
              shift: teamDetails.shift,
              startDate: teamDetails.start_date.split("T")[0],
              endDate: teamDetails.end_date.split("T")[0],
              courseId: teamDetails.course?.id
                ? {
                    label: teamDetails.course.name,
                    value: teamDetails.course.id,
                  }
                : null,
              unitId: teamDetails.unit?.id
                ? {
                    label: teamDetails.unit.name,
                    value: teamDetails.unit.id,
                  }
                : null,
            });
          }
        } catch (err) {
          if (isMounted) {
            setErrorMessages(["Erro ao carregar dados da turma"]);
          }
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      };

      loadTeamData();
      return () => {
        isMounted = false;
      };
    }
  }, [editMode, params.id, getTeamById]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);
      setErrorMessages([]);
      setSuccessMessage("");

      // Check for field errors
      if (Object.keys(fieldErrors).length > 0) {
        setErrorMessages(Object.values(fieldErrors));
        setIsLoading(false);
        return;
      }

      // Check for required fields
      if (
        !formData.name ||
        !formData.startDate ||
        !formData.endDate ||
        !formData.courseId ||
        !formData.unitId
      ) {
        setErrorMessages([
          "Todos os campos obrigatórios devem ser preenchidos.",
        ]);
        setIsLoading(false);
        return;
      }

      const teamData = {
        name: formData.name,
        is_first_semester: formData.period === "1",
        shift: formData.shift,
        start_date: formData.startDate,
        end_date: formData.endDate,
        course_id: formData.courseId.value.toString(),
        unit_id: formData.unitId.value.toString(),
      };

      try {
        if (editMode) {
          await updateTeam({ id: params.id!, ...teamData });
          setSuccessMessage("Turma atualizada com sucesso!");
          queryClient.invalidateQueries({ queryKey: ["teams"] });
          navigate("/turmas");
        } else {
          const newTeam = await createTeam(teamData);
          if (newTeam?.id) {
            setSuccessMessage("Turma criada com sucesso!");
            queryClient.invalidateQueries({ queryKey: ["teams"] });
              navigate("/turmas");
          } else {
            throw new Error("Falha ao criar nova turma");
          }
        }
      } catch (err: any) {
        setErrorMessages([
          "Erro ao salvar a turma. Por favor, tente novamente.",
          err.message || err.toString(),
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      formData,
      editMode,
      params.id,
      updateTeam,
      createTeam,
      queryClient,
      navigate,
      fieldErrors,
    ]
  );

  return (
    <Row>
      <Col className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card">
            <h4 className="mt-0 header-title">
              {editMode ? "Editar" : "Cadastrar"} Turma
            </h4>
            <p>
              {editMode
                ? "Edite uma turma do seu sistema"
                : "Cadastre as turmas do seu sistema"}
            </p>

            {errorMessages.length > 0 && (
              <Alert
                variant="danger"
                className="d-flex align-items-center gap-2"
              >
                <AlertCircle size={20} />
                <div>
                  {errorMessages.map((msg, idx) => (
                    <div key={idx}>{msg}</div>
                  ))}
                </div>
              </Alert>
            )}

            {successMessage && (
              <Alert
                variant="success"
                className="d-flex align-items-center gap-2"
              >
                <CheckCircle2 size={20} />
                {successMessage}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Nome <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      required
                      minLength={3}
                      placeholder="Nome da turma"
                      value={formData.name}
                      onChange={(e) => handleFormChange("name", e.target.value)}
                    />
                    {fieldErrors.name && (
                      <div className="text-danger">{fieldErrors.name}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label htmlFor="period-select">
                      Período <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      id="period-select"
                      value={formData.period}
                      onChange={(e) =>
                        handleFormChange("period", e.target.value)
                      }
                    >
                      <option value="1">1º Semestre</option>
                      <option value="2">2º Semestre</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>
                      Turno <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={formData.shift}
                      onChange={(e) =>
                        handleFormChange("shift", e.target.value)
                      }
                    >
                      {SHIFTS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Curso <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      options={courseOptions}
                      value={formData.courseId}
                      onChange={(option) =>
                        handleFormChange("courseId", option)
                      }
                      placeholder="Selecione o curso"
                      isClearable
                    />
                    {fieldErrors.courseId && (
                      <div className="text-danger">{fieldErrors.courseId}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label htmlFor="unit-select">
                      Unidade <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      inputId="unit-select"
                      options={unitOptions}
                      value={formData.unitId}
                      onChange={(option) => handleFormChange("unitId", option)}
                      placeholder="Selecione a unidade"
                      isClearable
                    />
                    {fieldErrors.unitId && (
                      <div className="text-danger">{fieldErrors.unitId}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Data de início <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      required
                      type="date"
                      value={formData.startDate}
                      onChange={(e) =>
                        handleFormChange("startDate", e.target.value)
                      }
                      max={formData.endDate || undefined}
                    />
                    {fieldErrors.startDate && (
                      <div className="text-danger">{fieldErrors.startDate}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Data de conclusão <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      required
                      type="date"
                      value={formData.endDate}
                      onChange={(e) =>
                        handleFormChange("endDate", e.target.value)
                      }
                      min={formData.startDate || undefined}
                    />
                    {fieldErrors.endDate && (
                      <div className="text-danger">{fieldErrors.endDate}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center mt-4">
                <Button
                  variant="secondary"
                  onClick={() => navigate("/turmas")}
                  className="d-flex align-items-center gap-2"
                >
                  <ArrowLeft size={20} />
                  Voltar
                </Button>

                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isLoading}
                    className="d-flex align-items-center gap-2"
                  >
                    {isLoading ? <Spinner size="sm" /> : <Save size={20} />}
                    Salvar
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </Col>

    </Row>
  );
}
